import React, { useState, useEffect } from 'react';
import { Users, Wallet, AlertTriangle, ArrowUpRight, BarChart as BarChartIcon, PieChart as PieChartIcon, MoreVertical, ClipboardList, Wrench, Zap, Droplets, Brush } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from 'recharts';
import toast from 'react-hot-toast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import styles from './Reports.module.css';

const COLORS = ['#154530', '#b45309', '#0284c7', '#ea580c', '#6366f1'];

const Reports = () => {
  const [data, setData] = useState({
    totalOccupancy: 0,
    occupancyTrend: 0,
    totalResidents: 0,
    maintenanceCollection: 0,
    maintenanceTrend: 0,
    pendingComplaints: 0,
    highPriorityComplaints: 0,
    monthlyCollectionData: [],
    complaintsByCategory: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await api.get('/admin/reports');
        setData(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load reports');
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading reports...</div>;
  }

  const formatAmount = (amt) => {
    if (amt >= 100000) return `₹${(amt / 100000).toFixed(1)}L`;
    if (amt >= 1000) return `₹${(amt / 1000).toFixed(1)}K`;
    return `₹${amt}`;
  };

  const totalComplaints = data.complaintsByCategory.reduce((sum, item) => sum + item.value, 0);

  const getCategoryIcon = (name) => {
    const n = name.toLowerCase();
    if (n.includes('clean')) return <Brush size={20} />;
    if (n.includes('electric')) return <Zap size={20} />;
    if (n.includes('plumb')) return <Droplets size={20} />;
    return <Wrench size={20} />;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Society Reports & Analytics" 
        breadcrumb={['Account', 'Reports']} 
      />

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.iconWrapper}><Users size={24} color="#154530" /></div>
          <div className={styles.statInfo}>
            <span>Total Occupancy</span>
            <h2>{data.totalOccupancy}%</h2>
            <small className={styles.positive}><ArrowUpRight size={12} /> {data.occupancyTrend}% this month</small>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.iconWrapper}><Wallet size={24} color="#154530" /></div>
          <div className={styles.statInfo}>
            <span>Maintenance Collection</span>
            <h2>{formatAmount(data.maintenanceCollection)}</h2>
            <small className={styles.positive}><ArrowUpRight size={12} /> {data.maintenanceTrend}% this month</small>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.iconWrapper}><AlertTriangle size={24} color="#b45309" /></div>
          <div className={styles.statInfo}>
            <span>Pending Complaints</span>
            <h2>{data.pendingComplaints}</h2>
            {data.highPriorityComplaints > 0 ? (
               <small className={styles.negative}>{data.highPriorityComplaints} High Priority</small>
            ) : (
               <small style={{ color: '#6b7280' }}>All clear</small>
            )}
          </div>
        </div>
      </div>

      <div className={styles.chartsGrid}>
        <div className={styles.premiumChartCard}>
          <div className={styles.premiumCardHeader}>
            <div className={styles.premiumHeaderLeft}>
              <div className={styles.premiumHeaderIcon} style={{ background: '#154530' }}>
                <Wallet size={24} color="#ffffff" />
              </div>
              <div className={styles.premiumHeaderTitle}>
                <h3>Monthly Maintenance Collection</h3>
                <p>Track monthly maintenance collection trends</p>
              </div>
            </div>
            <button className={styles.btnMore}>
              <MoreVertical size={20} color="#6b7280" />
            </button>
          </div>

          <div className={styles.collectionSummary}>
            <div className={styles.collectionSummaryLeft}>
              <div className={styles.rupeeCircle}>₹</div>
              <div className={styles.collectionSummaryText}>
                <span>Total Collection (This Month)</span>
                <h4>{formatAmount(data.maintenanceCollection)}</h4>
              </div>
            </div>
            <div className={styles.collectionSummaryRight}>
              <div className={styles.trendBadge}>
                <ArrowUpRight size={14} /> {data.maintenanceTrend}%
              </div>
              <span className={styles.vsText}>vs Last Month</span>
            </div>
          </div>

          <div className={styles.barChartWrapper}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthlyCollectionData} margin={{ top: 30, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 13 }} dy={10} />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tickFormatter={(value) => `₹${value/1000}k`} 
                  tick={{ fill: '#64748b', fontSize: 13 }}
                  label={{ value: 'Collection (₹)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 13, dy: 50, dx: -10 }}
                />
                <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(value) => `₹${value}`} />
                <Bar dataKey="amount" fill="#366847" radius={[6, 6, 0, 0]} barSize={40}>
                  <LabelList dataKey="amount" position="top" formatter={(val) => `₹${val/1000}k`} fill="#366847" fontSize={12} fontWeight="bold" dy={-5} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className={styles.premiumFooterBox}>
            <div className={styles.footerIconBox}>
              <BarChartIcon size={16} color="#154530" />
            </div>
            <div className={styles.footerTextContainer}>
              <span className={styles.footerTitle}>Data updated just now</span>
              <span className={styles.footerSub}>Last updated today</span>
            </div>
          </div>
        </div>
        <div className={styles.premiumChartCard}>
          <div className={styles.premiumCardHeader}>
            <div className={styles.premiumHeaderLeft}>
              <div className={styles.premiumHeaderIcon}>
                <PieChartIcon size={24} color="#154530" />
              </div>
              <div className={styles.premiumHeaderTitle}>
                <h3>Complaints by Category</h3>
                <p>Overview of complaints distribution</p>
              </div>
            </div>
            <button className={styles.btnMore}>
              <MoreVertical size={20} color="#6b7280" />
            </button>
          </div>

          <div className={styles.premiumChartWrapper}>
            {data.complaintsByCategory.length > 0 ? (
              <>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <defs>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
                        </filter>
                      </defs>
                      <Pie
                        data={data.complaintsByCategory}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={4}
                        cornerRadius={4}
                        fill="#8884d8"
                        dataKey="value"
                        stroke="none"
                        labelLine={false}
                        label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 20; // push outside
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          if (percent < 0.05) return null;
                          const color = COLORS[index % COLORS.length];
                          
                          // Width of the pill
                          const width = 50;
                          const height = 28;
                          const rectX = x - width / 2;
                          const rectY = y - height / 2;

                          return (
                            <g>
                              <rect x={rectX} y={rectY} width={width} height={height} fill="white" rx={14} filter="url(#shadow)" />
                              <text x={x} y={y} fill={color} textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight="bold">
                                {`${(percent * 100).toFixed(0)}%`}
                              </text>
                            </g>
                          );
                        }}
                      >
                        {data.complaintsByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} Complaints`} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  <div className={styles.chartCenterText}>
                    <ClipboardList size={32} color="#6b7280" />
                    <span>Total Complaints</span>
                    <strong>{totalComplaints}</strong>
                  </div>
                </div>

                <div className={styles.customLegend}>
                  {data.complaintsByCategory.map((entry, index) => {
                    const color = COLORS[index % COLORS.length];
                    const percent = totalComplaints > 0 ? ((entry.value / totalComplaints) * 100).toFixed(0) : 0;
                    return (
                      <div key={index} className={styles.legendCard} style={{ backgroundColor: `${color}10` }}>
                        <div className={styles.legendIconContainer} style={{ backgroundColor: color }}>
                          {getCategoryIcon(entry.name)}
                        </div>
                        <div className={styles.legendDetails}>
                          <span className={styles.legendName} style={{ color }}>{entry.name}</span>
                          <div className={styles.legendBottomRow}>
                            <span className={styles.legendValue}>{entry.value}</span>
                            <span className={styles.legendPercent} style={{ color }}>{percent}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <BarChartIcon size={48} />
                <p>No complaints data yet</p>
              </div>
            )}
          </div>

          <div className={styles.premiumCardFooter}>
            <BarChartIcon size={14} color="#9ca3af" />
            <span>Data updated just now</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
